<template>
	<span v-if="props.type == 'project'" class="__status" :class="_class" :title="tooltip">
		<Disc2 class="__status_icon" />
	</span>
	<span v-else-if="props.type == 'location'" class="__status" :class="_class" :title="tooltip">
		<MapPin class="__status_icon" />
		<!-- < class="__status_icon" /> -->
	</span>
	<span v-else-if="props.type == 'hub'" class="__status" :class="_class" :title="tooltip">
		<Boxes class="__status_icon" />
	</span>
	<span v-else-if="props.type == 'module'" class="__status" :class="_class" :title="tooltip">
		<Hexagon class="__status_icon" />
	</span>
	<span v-else-if="props.type == 'comapp'" class="__status" :class="_class" :title="tooltip">
		<RadioReceiver class="__status_icon" />
	</span>
	<span v-else-if="props.type == 'graph'" class="__status text-secondary">
		<ChartSpline class="__status_icon" />
	</span>
	<span v-else-if="props.type == 'sensor'" class="__status_icon">
		<Asterisk class="__status_icon" />
	</span>
</template>
<script setup lang="ts">
	import { computed, ref } from 'vue'
	import { useI18n } from 'vue-i18n'

	import { useBaseStore, type StateReason } from '@/stores/base.store'
	import { Boxes, ChartSpline, Disc2, Hexagon, MapPin, RadioReceiver, Asterisk } from 'lucide-vue-next'

	const baseStore = useBaseStore()
	const allStates = computed(() => baseStore.allStates)

	const _status = computed(() => allStates.value?.[props.type + 's']?.[props._id] || 'unknown')
	const _class = computed(() => {
		if (!_status.value || _status.value == 'unknown') return '_white'
		else if (_status.value == 'ok') return '_green'
		else if (_status.value == 'alert') return '_redBlink'
		else if (_status.value == 'warning') return '_orange'
		else if (_status.value == 'deactivated') return '_black'
		else if (_status.value == 'no_modules') return '_greym'
		else if (_status.value == 'no_hubs') return '_greyl'
		else if (_status.value == 'no_locations') return '_greyd'
		return ''
	})
	// const _class = computed(() => statusLightClass(_status.value))

	// Tooltip: what the colour means and — for orange/red — which modules or
	// comapps cause it, so a red project can be traced without clicking through.
	const { t: _tr } = useI18n({
		legacy: false,
		messages: {
			en: {
				state: {
					ok: 'Data up to date',
					warning: 'Orange: data timeout exceeded',
					alert: 'Red: twice the data timeout exceeded',
					deactivated: 'Deactivated',
					no_modules: 'No modules assigned',
					no_hubs: 'No hubs assigned',
					no_hubs_no_comapps: 'No hubs or ComApps assigned',
					no_locations: 'No locations assigned',
					unknown: 'State unknown',
				},
				module: 'module',
				comapp: 'ComApp',
				silent: '{name} ({kind}): last log {age} ago',
				timeout: 'data timeout {timeout} → orange after {timeout}, red after {double}',
				never: '{name} ({kind}): no data recorded yet',
				stale: '→ probably removed; deactivating it ends the red',
				more: '… and {n} more',
			},
			de: {
				state: {
					ok: 'Daten aktuell',
					warning: 'Orange: Daten-Timeout überschritten',
					alert: 'Rot: doppeltes Daten-Timeout überschritten',
					deactivated: 'Deaktiviert',
					no_modules: 'Keine Module zugeordnet',
					no_hubs: 'Keine Hubs zugeordnet',
					no_hubs_no_comapps: 'Keine Hubs oder ComApps zugeordnet',
					no_locations: 'Keine Locations zugeordnet',
					unknown: 'Zustand unbekannt',
				},
				module: 'Modul',
				comapp: 'ComApp',
				silent: '{name} ({kind}): letztes Log vor {age}',
				timeout: 'Daten-Timeout {timeout} → orange ab {timeout}, rot ab {double}',
				never: '{name} ({kind}): noch nie Daten erhalten',
				stale: '→ vermutlich ausgebaut; Deaktivieren beendet die Rotfärbung',
				more: '… und {n} weitere',
			},
		},
	})
	const duration = (min: number): string => {
		if (min < 120) return `${Math.max(1, Math.round(min))} min`
		if (min < 48 * 60) return `${Math.round(min / 60)} h`
		return `${Math.round(min / 1440)} d`
	}
	/** A configured limit: whole days or hours stay as such (60 → "1 h", 2880 → "2 d"). */
	const limit = (min: number): string => {
		if (min >= 1440 && min % 1440 === 0) return `${min / 1440} d`
		if (min >= 60 && min % 60 === 0) return `${min / 60} h`
		return `${min} min`
	}
	const reasonLine = (r: StateReason): string => {
		const kind = _tr(r.kind)
		if (!r.lastLog) return `${_tr('never', { name: r.name, kind })}\n   ${_tr('stale')}`
		const age = duration((Date.now() - new Date(r.lastLog).getTime()) / 60_000)
		const line = _tr('silent', { name: r.name, kind, age })
		const limits = _tr('timeout', { timeout: limit(r.alertInterval), double: limit(r.alertInterval * 2) })
		return `${line}\n   ${limits}${r.stale ? `\n   ${_tr('stale')}` : ''}`
	}
	const tooltip = computed(() => {
		const head = _tr(`state.${_status.value}`)
		const reasons = allStates.value?.reasons?.[props._id]
		if (!reasons?.items?.length) return head
		const lines = reasons.items.map((r: StateReason) => `• ${reasonLine(r)}`)
		if (reasons.total > reasons.items.length) lines.push(_tr('more', { n: reasons.total - reasons.items.length }))
		return [head, ...lines].join('\n')
	})
	const props = defineProps<{
		type: string // 'project' | 'location' | 'hub' | 'module' | 'comapp' | 'graph' | 'sensor'
		_id: string
	}>()
</script>

<style scoped>
	@reference '@/styles/tailwind.css';

	.__status {
		@apply relative inline-block;
		/* width: 1em;
		height: 1em; */

		/* .__status_icon {
			width: 1em;
			height: 1em;
		} */

		&.__ok {
			@apply text-teal-500;
		}

		&.__preWarn {
			@apply text-lime-400;
		}

		&.__warn {
			@apply text-amber-500;
		}

		&.__preAlert {
			@apply text-orange-600;
		}

		&.__warnPreAlert {
			@apply text-orange-800;
		}

		&.__alert {
			@apply text-red-600;
		}

		&._large {
			width: 18px;
			height: 18px;
			min-width: 18px;
		}

		&._green {
			@apply text-green-500;
		}

		&._red {
			@apply text-red-500;
		}

		&._redBlink {
			@apply text-orange-600;
			animation-duration: 1000ms;
			animation-name: blink;
			animation-iteration-count: infinite;
			animation-direction: alternate;
		}

		&._blue {
			@apply text-cyan-600;
		}

		&._orange {
			@apply text-amber-500;
		}

		&._white {
			@apply text-white;
		}

		&._black {
			@apply text-slate-500/60;
		}

		&._greyl {
			@apply text-gray-400;
		}

		&._greym {
			@apply text-gray-600;
		}

		&._greyd {
			@apply text-gray-800;
		}
	}
</style>
